import React, { useEffect, useMemo } from 'react';

import PropTypes from 'prop-types';
import { Field, Form, FormSpy } from 'react-final-form';
import { OnChange } from 'react-final-form-listeners';
import { FormattedMessage, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';

import { Button, FormDatePicker, FormField, Modal } from 'components';
import { COUPON_EXPIRY_TYPES } from 'coupon-common';
import {
  actions as couponBuilderActions,
  duck as couponBuilderDuck,
} from 'coupon-common/src/modules/couponBuilder';
import { actions } from 'coupon-common/src/modules/coupons';
import {
  composeSyncValidators,
  dispatchAsAsync,
  formatToISOString,
  isInteger,
  isPositive,
  normalizeInteger,
  parseISOString,
  required,
} from 'coupon-common/src/utils';

import messages from './messages';

// displaying field errors after submitting the form to maintain size of the form while user filling it
const computeFieldDisplayError = meta =>
  !!((meta.error || meta.submitError) && meta.submitFailed);

/**
 * Native radio group (does not depend on FormRadio)
 * This fixes the "no radio buttons" regression and restores Date + Amount Limit options.
 */
const ExpiryTypeRadioGroup = ({ intl, disabled }) => (
  <Field name="expiry_type">
    {({ input }) => {
      // Fall back to string literals if constants ever differ/missing
      const amountValue = COUPON_EXPIRY_TYPES?.AMOUNT_LIMIT || 'amount_limit';
      const dateValue = COUPON_EXPIRY_TYPES?.DATE || 'date';

      return (
        <div className="d-flex" style={{ gap: 18 }}>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              margin: 0,
              cursor: disabled ? 'not-allowed' : 'pointer',
            }}
          >
            <input
              type="radio"
              name={input.name}
              value={amountValue}
              checked={input.value === amountValue}
              onChange={() => input.onChange(amountValue)}
              disabled={disabled}
            />
            {intl.formatMessage(messages.expiryTypeAmountLimitLabel)}
          </label>

          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              margin: 0,
              cursor: disabled ? 'not-allowed' : 'pointer',
            }}
          >
            <input
              type="radio"
              name={input.name}
              value={dateValue}
              checked={input.value === dateValue}
              onChange={() => input.onChange(dateValue)}
              disabled={disabled}
            />
            {intl.formatMessage(messages.expiryTypeDateLabel)}
          </label>
        </div>
      );
    }}
  </Field>
);

ExpiryTypeRadioGroup.propTypes = {
  intl: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
};

ExpiryTypeRadioGroup.defaultProps = {
  disabled: false,
};

export const CouponRepublish = ({
  intl,
  dispatch,
  locale,
  couponBuilderState,
  republishCouponId,
  isOpen,
  closeRepublishCouponHandler,
}) => {
  useEffect(() => {
    if (republishCouponId) {
      dispatch(couponBuilderActions.getCoupon(republishCouponId));
    }
  }, [dispatch, republishCouponId]);

  const { data: republishCouponData, fetching: republishCouponDataFetching } =
    couponBuilderState?.[republishCouponId] ||
    couponBuilderDuck.INITIAL_COUPON_ITEM_STATE;

  const republishInitialValues = useMemo(() => {
    const dateValue = COUPON_EXPIRY_TYPES?.DATE || 'date';

    if (!republishCouponData) {
      return { expiry_type: dateValue };
    }

    return {
      expiry_type: republishCouponData.expiry_type || dateValue,
      expiration_date: republishCouponData.expiration_date || null,
      expiration_quantity: republishCouponData.expiration_quantity || null,
    };
  }, [republishCouponData]);

  const handleRepublish = async data => {
    closeRepublishCouponHandler();

    // Convert subcategories from objects to IDs if needed
    const transformedSubcategories = (data.subcategories || []).map(sc =>
      typeof sc === 'object' ? sc.id : sc,
    );

    const payload = {
      ...republishCouponData,
      ...data,
      subcategories: transformedSubcategories,
    };

    const { success } = await dispatchAsAsync(actions.republishCoupon(payload), dispatch);

    if (success) {
      toast(
        <FormattedMessage
          {...messages.couponRepublishSuccessText}
          values={{
            title: <b>{republishCouponData?.title}</b>,
          }}
        />,
      );
    }
  };

  const renderCouponExpiryTypeField = expiry_type => {
    const amountValue = COUPON_EXPIRY_TYPES?.AMOUNT_LIMIT || 'amount_limit';
    const dateValue = COUPON_EXPIRY_TYPES?.DATE || 'date';

    if (expiry_type === amountValue) {
      return (
        <Field
          name="expiration_quantity"
          label={intl.formatMessage(messages.expirationQuantityFieldLabel)}
          validate={composeSyncValidators(required, isPositive, isInteger)}
          component={FormField}
          parse={normalizeInteger}
          readOnly={republishCouponDataFetching}
          computeDisplayError={computeFieldDisplayError}
        />
      );
    }

    if (expiry_type === dateValue) {
      return (
        <Field
          name="expiration_date"
          label={intl.formatMessage(messages.expirationDateFieldLabel)}
          validate={required}
          format={value => parseISOString(value)}
          parse={value => formatToISOString(value)}
          locale={locale}
          component={FormDatePicker}
          readOnly={republishCouponDataFetching}
          computeDisplayError={computeFieldDisplayError}
        />
      );
    }

    return null;
  };

  return (
    <Modal
      title={intl.formatMessage(messages.pageHeadingText)}
      isOpen={isOpen}
      onRequestClose={closeRepublishCouponHandler}
    >
      <Form
        onSubmit={handleRepublish}
        initialValues={republishInitialValues}
        keepDirtyOnReinitialize={false}
        render={({ handleSubmit }) => (
          <>
            <h6 className="pt-2">
              <FormattedMessage {...messages.couponTypeFieldsGroupLabel} />
            </h6>

            <ExpiryTypeRadioGroup
              intl={intl}
              disabled={republishCouponDataFetching}
            />

            <FormSpy
              subscription={{ values: true }}
              render={({ values: { expiry_type } }) =>
                renderCouponExpiryTypeField(expiry_type)
              }
            />

            <FormSpy
              render={({ form }) => (
                <OnChange name="expiry_type">
                  {value => {
                    const amountValue =
                      COUPON_EXPIRY_TYPES?.AMOUNT_LIMIT || 'amount_limit';
                    const dateValue = COUPON_EXPIRY_TYPES?.DATE || 'date';

                    if (value === dateValue) {
                      form.change('expiration_quantity', null);
                    } else if (value === amountValue) {
                      form.change('expiration_date', null);
                    }
                  }}
                </OnChange>
              )}
            />

            <Button onClick={handleSubmit} fetching={republishCouponDataFetching}>
              <FormattedMessage {...messages.republishButtonText} />
            </Button>
          </>
        )}
      />
    </Modal>
  );
};

CouponRepublish.propTypes = {
  intl: PropTypes.object,
  dispatch: PropTypes.func,
  locale: PropTypes.string,
  couponBuilderState: PropTypes.object,
  republishCouponId: PropTypes.number.isRequired,
  isOpen: PropTypes.bool.isRequired,
  closeRepublishCouponHandler: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  couponBuilderState: state.couponBuilder,
  locale: state.language.locale,
});

export default connect(mapStateToProps)(injectIntl(CouponRepublish));
